import { useEffect, useRef } from 'react';
import './BookingModal.css';

// Overridable at build time via VITE_CAL_URL (see .env.example) — falls back
// to the shared link if the variable isn't set, so a fresh checkout still
// works with no configuration.
export const BOOKING_URL = import.meta.env.VITE_CAL_URL || 'https://cal.eu/dedupr/30min';

export interface BookingModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Cal.eu is a self-hosted Cal.com instance, which posts a
 * `{ type: "CAL:booking_successful" | "BOOKING_SUCCESSFUL", ... }`-shaped
 * message to its embedding window once a slot is confirmed — the same
 * protocol Cal.com's own embed snippet relies on, whether or not that
 * snippet is actually loaded. Matched loosely (case-insensitive substring on
 * `type`) because the exact casing has changed across Cal.com versions and a
 * missed exact match would silently break auto-close rather than degrade —
 * the close button below always works regardless, so a miss here only costs
 * the auto-close convenience, never the ability to leave the modal.
 */
function looksLikeBookingSuccess(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const type = (data as { type?: unknown }).type;
  if (typeof type !== 'string') return false;
  const normalized = type.toLowerCase();
  return normalized.includes('booking') && normalized.includes('success');
}

export function BookingModal({ open, onClose }: BookingModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!open) return;
    function onMessage(event: MessageEvent) {
      if (looksLikeBookingSuccess(event.data)) onClose();
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [open, onClose]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    if (open) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="land-booking-overlay" role="dialog" aria-modal="true" aria-label="Book a meeting">
      <div className="land-booking">
        <div className="land-booking__header">
          <div>
            <div className="land-booking__title">Book a meeting</div>
            <div className="land-booking__subtitle">30 minutes · closes automatically once booked</div>
          </div>
          <button className="land-icon-btn" style={{ width: 32, height: 32 }} aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="land-booking__body">
          <iframe
            ref={iframeRef}
            src={BOOKING_URL}
            title="Book a meeting with Dedupr"
            className="land-booking__iframe"
          />
        </div>
        <div className="land-booking__footer">
          <span>Trouble loading the calendar?</span>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            Open in a new tab ↗
          </a>
        </div>
      </div>
    </div>
  );
}
